<!--
  Gestione Prodotti — main inventory page.
  Redesigned 2026-06-11 (v2.7.0, Phase 1.5 Chunk B.1 + polish).

  Dual view:
    • Vista Prodotti   — Diakonix table with client-side live filter
                         + pagination (50/page). Click row → detail
                         modal (Chunk B.3).
    • Vista Categorie  — expandable cards Family → Sub-family →
                         Group → Sub-group, click leaf → filtered
                         list (gestione-prodotti-dettagli).

  Drops kendo-grid + mat-spinner + the ~50 inline `style="..."`
  attributes. Search runs live as the user types (no Enter / Cerca
  button needed) and uses client-side string includes — fixes the
  SQL `_` wildcard bug from the legacy backend search.
-->

<div class="inventory-page">

  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="inventory-header">
    <div class="inventory-header__titles">
      <h1 class="inventory-header__title">
        @if (viewMode === 'products') { 📦 Inventario prodotti }
        @else { 🗂 Catalogo categorie }
      </h1>
      <p class="inventory-header__subtitle">
        @if (viewMode === 'products') {
          @if (stockProducts.length > 0) {
            <strong>{{ filteredProducts.length }}</strong>
            {{ filteredProducts.length === 1 ? 'prodotto' : 'prodotti' }}
            @if (isSearched) {
              · su {{ stockProducts.length }} totali · filtro
              <strong>"{{ filterTerm }}"</strong>
            }
          } @else if (loading) {
            Caricamento inventario…
          } @else {
            Nessun prodotto in inventario
          }
        } @else {
          @if (categories.length > 0) {
            <strong>{{ categories.length }}</strong>
            {{ categories.length === 1 ? 'categoria' : 'categorie' }}
            @if (isSearched) { · filtro <strong>"{{ filterTerm }}"</strong> }
          } @else if (loading) {
            Caricamento categorie…
          } @else {
            Nessuna categoria in catalogo
          }
        }
      </p>
    </div>

    <div class="inventory-header__controls">
      <!-- View toggle pill -->
      <div class="view-toggle" role="tablist" aria-label="Vista inventario">
        <button
          type="button"
          class="view-toggle__btn"
          [class.view-toggle__btn--active]="viewMode === 'products'"
          (click)="setView('products')"
          role="tab"
        >
          📦 Prodotti
        </button>
        <button
          type="button"
          class="view-toggle__btn"
          [class.view-toggle__btn--active]="viewMode === 'categories'"
          (click)="setView('categories')"
          role="tab"
        >
          🗂 Categorie
        </button>
      </div>
    </div>
  </header>

  <!-- ─── Filter bar — live, no Cerca button ───────────────────── -->
  <section class="inventory-filters" aria-label="Filtri ricerca">
    <div class="filter-field">
      <label class="filter-field__label" for="searchInventory">
        @if (viewMode === 'products') { Cerca prodotto }
        @else { Cerca categoria }
      </label>
      <div class="filter-field__input-wrap">
        <input
          id="searchInventory"
          type="search"
          class="filter-field__input"
          [placeholder]="
            viewMode === 'products'
              ? 'Codice o descrizione prodotto…'
              : 'Nome categoria…'
          "
          [(ngModel)]="filterTerm"
          (ngModelChange)="onSearchChange()"
        />
        @if (filterTerm) {
          <button
            type="button"
            class="filter-field__clear"
            (click)="clearSearch()"
            aria-label="Rimuovi filtro"
          >×</button>
        }
      </div>
    </div>
  </section>

  <!-- ─── Body: Vista Prodotti (table + pagination) ────────────── -->
  @if (viewMode === 'products') {
    <section class="inventory-table-card" aria-label="Elenco prodotti">

      @if (loading) {
        <div class="table-skeleton" aria-busy="true">
          @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
            <div class="table-skeleton__row">
              <div class="table-skeleton__line table-skeleton__line--lg"></div>
              <div class="table-skeleton__line table-skeleton__line--md"></div>
              <div class="table-skeleton__line table-skeleton__line--sm"></div>
            </div>
          }
        </div>
      } @else if (filteredProducts.length === 0) {
        <div class="inventory-empty">
          <div class="inventory-empty__icon" aria-hidden="true">
            @if (isSearched) { 🔎 } @else { 📭 }
          </div>
          <p class="inventory-empty__msg">
            @if (isSearched) {
              Nessun prodotto corrisponde a <strong>"{{ filterTerm }}"</strong>.
            } @else {
              Nessun prodotto in inventario.
            }
          </p>
        </div>
      } @else {
        <div class="inventory-table-scroll">
          <table class="inventory-table" role="table">
            <thead>
              <tr>
                <th class="col-code sortable" (click)="sort('pr_code')">
                  Codice <span class="sort-icon">{{ sortIcon('pr_code') }}</span>
                </th>
                <th class="col-desc sortable" (click)="sort('pr_description')">
                  Descrizione <span class="sort-icon">{{ sortIcon('pr_description') }}</span>
                </th>
                <th class="col-num sortable" (click)="sort('pr_stock')">
                  Stock <span class="sort-icon">{{ sortIcon('pr_stock') }}</span>
                </th>
                <th class="col-num sortable" (click)="sort('committed')">
                  Impegnato <span class="sort-icon">{{ sortIcon('committed') }}</span>
                </th>
                <th class="col-num sortable" (click)="sort('available')">
                  Disponibile <span class="sort-icon">{{ sortIcon('available') }}</span>
                </th>
                <th class="col-actions">Azioni</th>
              </tr>
            </thead>
            <tbody>
              @for (
                item of pagedProducts;
                track item.pr_code + '-' + ($index)
              ) {
                <tr
                  class="inventory-row"
                  [class.inventory-row--low]="
                    (+item.available || 0) <= 0 &&
                    (+item.pr_stock || 0) > 0
                  "
                  [class.inventory-row--out]="(+item.pr_stock || 0) <= 0"
                  (click)="openDetail(item.pr_code, item.pr_description)"
                  title="Apri dettagli prodotto"
                >
                  <td class="col-code">
                    <span class="cell-code">{{ item.pr_code || '—' }}</span>
                  </td>
                  <td class="col-desc">
                    <span class="cell-desc">{{ item.pr_description || '—' }}</span>
                  </td>
                  <td class="col-num">
                    <span class="cell-num">{{ item.pr_stock ?? 0 }}</span>
                  </td>
                  <td class="col-num">
                    <span class="cell-num cell-num--muted">
                      {{ item.committed ?? 0 }}
                    </span>
                  </td>
                  <td class="col-num">
                    <span
                      class="cell-num cell-num--strong"
                      [class.cell-num--ok]="(+item.available || 0) > 0"
                      [class.cell-num--danger]="(+item.available || 0) <= 0"
                    >{{ item.available ?? 0 }}</span>
                  </td>
                  <td class="col-actions" (click)="$event.stopPropagation()">
                    <button
                      type="button"
                      class="icon-btn"
                      title="Apri dettagli"
                      (click)="openDetail(item.pr_code, item.pr_description)"
                    >ℹ️</button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <!-- ─── Pagination ─── -->
        @if (totalPages > 1) {
          <nav class="pagination" aria-label="Paginazione inventario">
            <button
              type="button"
              class="pagination__btn"
              [disabled]="currentPage === 0"
              (click)="prevPage()"
            >‹ Precedente</button>
            <span class="pagination__info">
              Pagina <strong>{{ currentPage + 1 }}</strong> di {{ totalPages }}
              · <span class="pagination__total">
                {{ filteredProducts.length }}
                {{ filteredProducts.length === 1 ? 'prodotto' : 'prodotti' }}
              </span>
            </span>
            <button
              type="button"
              class="pagination__btn"
              [disabled]="currentPage >= totalPages - 1"
              (click)="nextPage()"
            >Successiva ›</button>
          </nav>
        }
      }
    </section>
  }

  <!-- ─── Body: Vista Categorie (cards) ─────────────────────────── -->
  @if (viewMode === 'categories') {
    <section class="categories-wrap" aria-label="Catalogo categorie">

      @if (loading) {
        <div class="cards-skeleton" aria-busy="true">
          @for (i of [1, 2, 3, 4]; track i) {
            <div class="cards-skeleton__card">
              <div class="cards-skeleton__line cards-skeleton__line--lg"></div>
              <div class="cards-skeleton__line cards-skeleton__line--md"></div>
            </div>
          }
        </div>
      } @else if (
        (!isSearched && categories.length === 0) ||
        (isSearched && (filteredCategories?.length ?? 0) === 0)
      ) {
        <div class="inventory-empty">
          <div class="inventory-empty__icon" aria-hidden="true">
            @if (isSearched) { 🔎 } @else { 📭 }
          </div>
          <p class="inventory-empty__msg">
            @if (isSearched) {
              Nessuna categoria corrisponde a <strong>"{{ filterTerm }}"</strong>.
            } @else {
              Nessuna categoria in catalogo.
            }
          </p>
        </div>
      } @else {
        <div class="families-grid">
          @for (
            family of (isSearched ? filteredCategories : categories);
            track family.head_family + '-' + ($index);
            let i = $index
          ) {
            <article class="family-card">

              <!-- Family header (expand toggle) -->
              <button
                type="button"
                class="family-card__header"
                [class.family-card__header--open]="family.open"
                (click)="family.open = !family.open"
              >
                <span class="family-card__icon">📁</span>
                <span class="family-card__text">
                  <span class="family-card__name">{{ family.description }}</span>
                  <span class="family-card__meta">
                    {{ family.subFamilies?.length || 0 }}
                    {{
                      (family.subFamilies?.length || 0) === 1
                        ? 'sottocategoria'
                        : 'sottocategorie'
                    }}
                  </span>
                </span>
                <span
                  class="family-card__chevron"
                  [class.family-card__chevron--open]="family.open"
                >▾</span>
              </button>

              <!-- Family body (subfamilies) -->
              @if (family.open) {
                <div class="family-card__body">

                  @if (!family.subFamilies?.length) {
                    <button
                      type="button"
                      class="btn btn--ghost btn--sm"
                      (click)="goToProducts(family.head_family)"
                    >
                      → Vai ai prodotti
                    </button>
                  } @else {
                    <div class="subfamily-grid">
                      @for (
                        sub of family.subFamilies;
                        track sub.sub_family + '-' + ($index);
                        let j = $index
                      ) {
                        <section class="subfamily-card">
                          <header class="subfamily-card__header">
                            <h4 class="subfamily-card__name">
                              {{ sub.description }}
                            </h4>
                            <span class="subfamily-card__meta">
                              {{ sub.groups?.length || 0 }}
                              {{ (sub.groups?.length || 0) === 1 ? 'gruppo' : 'gruppi' }}
                            </span>
                          </header>

                          @if (!sub.groups?.length) {
                            <button
                              type="button"
                              class="btn btn--ghost btn--sm subfamily-card__action"
                              (click)="
                                goToProducts(
                                  family.head_family,
                                  sub.sub_family
                                )
                              "
                            >→ Vai ai prodotti</button>
                          } @else {
                            <ul class="groups-list" role="list">
                              @for (
                                group of sub.groups;
                                track group.head_group + '-' + ($index);
                                let k = $index
                              ) {
                                <li class="group-item">
                                  <button
                                    type="button"
                                    class="group-item__head"
                                    (click)="
                                      !group.subgroups?.length
                                        ? goToProducts(
                                            family.head_family,
                                            sub.sub_family,
                                            group.head_group
                                          )
                                        : null
                                    "
                                  >
                                    <span class="group-item__icon">📂</span>
                                    <span class="group-item__name">
                                      {{ group.description }}
                                    </span>
                                    @if (group.subgroups?.length) {
                                      <span class="group-item__count">
                                        {{ group.subgroups.length }}
                                      </span>
                                    } @else {
                                      <span class="group-item__arrow">→</span>
                                    }
                                  </button>

                                  @if (group.subgroups?.length) {
                                    <ul class="subgroups-list" role="list">
                                      @for (
                                        sg of group.subgroups;
                                        track sg.sub_group + '-' + ($index)
                                      ) {
                                        <li>
                                          <button
                                            type="button"
                                            class="subgroup-pill"
                                            (click)="
                                              goToProducts(
                                                family.head_family,
                                                sub.sub_family,
                                                group.head_group,
                                                sg.sub_group
                                              )
                                            "
                                          >
                                            {{ sg.description }}
                                            <span class="subgroup-pill__arrow">→</span>
                                          </button>
                                        </li>
                                      }
                                    </ul>
                                  }
                                </li>
                              }
                            </ul>
                          }
                        </section>
                      }
                    </div>
                  }
                </div>
              }

            </article>
          }
        </div>
      }
    </section>
  }

</div>
